import { ActivityTypeSchema, GetResponse, NightInput } from "./api";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n: number) => String(n).padStart(2, "0");

function utcOf(day: string): Date {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

export function addDays(day: string, n: number): string {
  const date = utcOf(day);
  date.setUTCDate(date.getUTCDate() + n);
  return date.toISOString().slice(0, 10);
}

export function weekRange(endDay: string): { from: string; to: string } {
  return { from: addDays(endDay, -6), to: endDay };
}

export function weekDays(endDay: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(endDay, i - 6));
}

export function dayLabel(day: string): string {
  const date = utcOf(day);
  return `${DAY_NAMES[date.getUTCDay()]} ${MONTH_NAMES[date.getUTCMonth()]} ${date.getUTCDate()}`;
}

export function nightTitle(day: string): string {
  return `${dayLabel(day)} → ${dayLabel(addDays(day, 1))}`;
}

export function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

export function fromMinutes(n: number): string {
  const wrapped = ((n % 1440) + 1440) % 1440;
  return `${pad(Math.floor(wrapped / 60))}:${pad(wrapped % 60)}`;
}

export function addMinutes(hhmm: string, n: number): string {
  return fromMinutes(toMinutes(hhmm) + n);
}

export function ampm(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  return `${h % 12 === 0 ? 12 : h % 12}:${pad(m)} ${h < 12 ? "AM" : "PM"}`;
}

/** Typed time ("10:40 pm", "2240", "7p") to "HH:mm", or null. */
export function parseTyped(text: string): string | null {
  const match = /^(\d{1,2})(?::?(\d{2}))?\s*(a|p|am|pm)?$/.exec(text.trim().toLowerCase());
  if (!match) return null;
  let h = Number(match[1]);
  const m = match[2] ? Number(match[2]) : 0;
  const suffix = match[3];
  if (suffix) {
    if (h === 12) h = 0;
    if (suffix[0] === "p") h += 12;
  }
  if (h >= 24 || m >= 60) return null;
  return `${pad(h)}:${pad(m)}`;
}

export function formatDuration(minutes: number): string {
  return `${Math.floor(minutes / 60)}h ${pad(minutes % 60)}m`;
}

/** The form state. awakeningCount is null until the patient answers (D14). */
export type NightDraft = Omit<NightInput, "awakeningCount"> & { awakeningCount: number | null };

export function DEFAULT_DRAFT(day: string): NightDraft {
  return {
    day,
    inBed: "22:30",
    sleepLatencyMinutes: 30,
    wake: "06:30",
    outOfBedAfterMinutes: 15,
    dayType: "work",
    awakeningCount: null,
    awakeningMinutes: 0,
    awakeningTimes: [],
    activities: [],
    notes: "",
  };
}

/**
 * A saved night gives its input; a schedule copy gives the schedule only (D14,
 * even if the server sends more); none gives the defaults. A null clock or
 * minutes value (a night converted from the legacy format) falls back to the default.
 */
export function toDraft(day: string, res: GetResponse): NightDraft {
  const base = DEFAULT_DRAFT(day);
  const schedule = (src: NonNullable<GetResponse["prefill"]>): NightDraft => ({
    ...base,
    inBed: src.inBed ?? base.inBed,
    sleepLatencyMinutes: src.sleepLatencyMinutes ?? base.sleepLatencyMinutes,
    wake: src.wake ?? base.wake,
    outOfBedAfterMinutes: src.outOfBedAfterMinutes ?? base.outOfBedAfterMinutes,
    dayType: src.dayType,
  });
  if (res.night) {
    const src = res.night.input;
    const activities: NightDraft["activities"] = [];
    for (const a of src.activities) {
      const type = ActivityTypeSchema.safeParse(a.type);
      if (type.success && a.start && a.end) activities.push({ type: type.data, start: a.start, end: a.end });
    }
    return {
      ...schedule(src),
      day,
      awakeningCount: src.awakeningCount,
      awakeningMinutes: src.awakeningMinutes ?? 0,
      awakeningTimes: src.awakeningTimes,
      activities,
      notes: src.notes,
    };
  }
  if (res.prefill) return schedule(res.prefill);
  return base;
}

export function toNightInput(draft: NightDraft): NightInput {
  if (draft.awakeningCount === null) throw new Error("awakeningCount is not answered");
  return { ...draft, awakeningCount: draft.awakeningCount };
}

export type NightPreview =
  | { ok: true; inBedAt: number; asleepAt: number; wakeAt: number; outAt: number; metrics: { asleepMinutes: number; inBedMinutes: number; efficiencyPct: number } }
  | { ok: false; field: "wake" | "awakeningMinutes" };

/**
 * Display-only preview of the server's night formula (D13), in wall-clock
 * minutes from 00:00 of `day`. Wall clock, not elapsed time: on a DST night it
 * can differ from the server by 60 minutes. The stored value wins.
 */
export function previewMetrics(draft: NightDraft): NightPreview {
  const inBedAt = toMinutes(draft.inBed) + (toMinutes(draft.inBed) >= 720 ? 0 : 1440);
  const asleepAt = inBedAt + draft.sleepLatencyMinutes;
  let wakeAt = toMinutes(draft.wake);
  while (wakeAt <= asleepAt) wakeAt += 1440;
  const outAt = wakeAt + draft.outOfBedAfterMinutes;
  if (wakeAt - inBedAt > 1440) return { ok: false, field: "wake" };
  if (draft.awakeningMinutes >= wakeAt - asleepAt) return { ok: false, field: "awakeningMinutes" };
  const asleepMinutes = Math.max(wakeAt - asleepAt - draft.awakeningMinutes, 0);
  const inBedMinutes = outAt - inBedAt;
  const efficiencyPct = inBedMinutes ? Math.round((asleepMinutes / inBedMinutes) * 100) : 0;
  return { ok: true, inBedAt, asleepAt, wakeAt, outAt, metrics: { asleepMinutes, inBedMinutes, efficiencyPct } };
}
